import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Chart from 'react-apexcharts';
import _ from 'lodash';
import { toast } from 'react-toastify';
import classes from './syncPakVsSortPak.module.scss';
import {
  getDashboardSyncPakVsSortPakRequest,
} from '../../../../store/actions/dashboard';
import { useQuery } from '../../../../utils/hooks/useQuery';
import SyncPakVsSortPakFilters from './SyncPakVsSortPakFilters/SyncPakVsSortPakFilters';
import utils from '../../../../utils/utils';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import Loader from '../../../_common/Loader/Loader';
import allergyIcon from '../../../../assets/icons/allergy_ellipse-green.svg';
import cardiologyIcon from '../../../../assets/icons/cardiology_ellipse-blueBorder.svg';
import { getHealthRequest } from '../../../../store/actions/healths';
import accountHelpers from '../../../../utils/accountHelpers';
import Api from '../../../../api/api';

const typeDataLeft = [
  {
    title: 'Patient',
    key: 'patient',
    value: 'patient',
  },
  {
    title: 'Script',
    key: 'script',
    value: 'script',
  },
];

const specType = [
  {
    name: 'SyncPak',
    color: '#00C974',
    image: allergyIcon,
    figure: 'ellipse',
  },

  {
    name: 'SortPak',
    color: '#405DF9',
    image: cardiologyIcon,
    figure: 'ellipse',
  },
];

const chartTemplate = {
  series: [],

  options: {
    chart: {
      height: 350,
      type: 'line',
      toolbar: {
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },

    colors: [],

    stroke: {
      curve: 'straight',
      width: 2,
    },

    tooltip: {
      shared: true,
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
      },
      y: {
        formatter(val) {
          const formatter = Intl.NumberFormat('en');
          return formatter.format(val);
        },
      },
    },

    dataLabels: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        columnWidth: '20%',
      },
    },

    annotations: {
      points: [],
    },

    markers: {
      size: 0,
      colors: ['transparent'],
      strokeWidth: 1,
      shape: ['circle'],
      hover: {
        sizeOffset: 0,
      },
    },
    grid: {
      borderColor: 'rgba(0, 0, 0, 0.1)',
      xaxis: {
        lines: {
          show: true,
        },
      },

      yaxis: {
        lines: {
          show: true,
        },
      },
    },

    legend: {
      show: false,
    },

    yaxis: {
      showForNullSeries: false,
      labels: {
        style: {
          colors: ['rgba(29, 39, 64, 0.5)'],
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          cssClass: 'apexcharts-yaxis-label',
        },

        formatter(val) {
          const formatter = Intl.NumberFormat('en', { notation: 'compact' });
          return formatter.format(val);
        },
      },
    },

    xaxis: {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          cssClass: 'apexcharts-xaxis-label',
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    },
  },
};

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment()
  .startOf('month')
  .format('YYYY-MM-DD');

const thisMonthEndDate = moment()
  .format('YYYY-MM-DD');

let firstLoad = true;

const SyncPakVsSortPak = () => {
  const {
    query,
    setQuery,
  } = useQuery();

  const dispatch = useDispatch();
  const { dashboardSyncPakVsSortPak } = useSelector((store) => store.dashboard);
  const { selectHealthList, healthTotalPage, healthCurrentPage } = useSelector((store) => store.healths);
  const [chart, setChart] = useState(chartTemplate);
  const [currentCheckedTypes, setCurrentCheckedTypes] = useState([]);
  const [currentTypes, setCurrentTypes] = useState([]);
  const [chartType, setChartType] = useState('patient');

  const [requestLoading, setRequestLoading] = useState(false);
  const [chartLoading, setChartsLoading] = useState(false);
  const [downloadLoad, setDownloadLoad] = useState(false);

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.end || thisMonthEndDate,
    state: query.state || '',
    health: query.health || '',
    healthName: query.healthName || '',
  });

  const { data, totalSortPak, totalSyncPak } = dashboardSyncPakVsSortPak;

  useEffect(() => {
    (async () => {
      setRequestLoading((prev) => !prev);
      await Promise.all([
        dispatch(getDashboardSyncPakVsSortPakRequest(collectRequestData(requestData))),
        dispatch(getHealthRequest({
          userId: accountHelpers.getUser().id,
          page: 1,
          limit: 20,
        }))]);

      setRequestLoading((prev) => !prev);
    })();

    return () => firstLoad = true;
  }, []);

  useEffect(() => {
    (async () => {
      if (!_.isEmpty(dashboardSyncPakVsSortPak)) {
        setChartsLoading((prev) => !prev);
        const newChart = { ...chartTemplate };
        const filteredData = data.filter((d) => d.pouch);
        const types = _.uniq(filteredData.map((d) => d.pouch)
          .filter((d) => specType.find((s) => s.name === d) !== d));

        if (_.isEmpty(data)) {
          setCurrentTypes(types);
        }
        if (firstLoad && !_.isEmpty(data)) {
          setCurrentCheckedTypes(types);
          setCurrentTypes(types);
          firstLoad = false;
          setChartsLoading((prev) => !prev);
          return;
        }

        const groupData = {
          SortPak: [],
          SyncPak: [],
        };

        filteredData.forEach((d) => {
          if (groupData[d.pouch].find((g) => g.start_date === d.start_date)) {
            groupData[d.pouch] = groupData[d.pouch].map((p) => (p.start_date === d.start_date
              ? { ...p, patient: +p.patient + (+d.patient), script: +p.script + (+d.script) }
              : p));
          } else {
            groupData[d.pouch] = [...groupData[d.pouch], d];
          }
        });

        const filteredTypes = specType.filter((t) => currentCheckedTypes.includes(t.name));
        if (_.isEmpty(data)) {
          newChart.series = [{
            name: '',
            data: [],
          }];
        } else {
          newChart.series = filteredTypes
            .map((t) => ({
              name: t.name,
              data: chart.options.xaxis.categories.map((c) => (groupData[t.name]?.find((d) => moment(d.start_date)
                .format('MMM') === c)
                ? groupData[t.name].find((d) => moment(d.start_date)
                  .format('MMM') === c)[chartType]
                : 0)),
            }));

          newChart.options.colors = filteredTypes.map((t) => t.color);
        }

        const points = [];

        newChart.series.forEach((t, index) => t.data.forEach((d, i) => {
          const thisType = filteredTypes[index];
          const figureSize = thisType.figure === 'triangle' ? 15 : thisType.figure === 'square' ? 12 : 13;

          if (d) {
            points.push({
              x: chart.options.xaxis.categories[i],
              y: d,
              marker: {
                size: 0,
              },
              image: {
                path: thisType.image,
                width: figureSize,
                height: figureSize,
                offsetX: 0,
                offsetY: 0,
              },
            });
          }
        }));

        newChart.options.annotations.points = points;
        await setChart(newChart);
        setChartsLoading((prev) => !prev);
      }
    })();
  }, [currentCheckedTypes, data, chartType]);

  const onChangeDate = useCallback((currentDate) => {
    setRequestData((prev) => ({
      ...prev,
      start: currentDate[0],
      end: currentDate[1],
    }));
  }, []);

  const updateFilters = useCallback(async (path) => {
    const dataRequest = collectRequestData(requestData);

    if (path === 'download') {
      try {
        setDownloadLoad(true);
        await Api.getDashboardSyncPakVsSortPak({ ...dataRequest, download: true });
        toast.success('Successfully downloaded file');
        setDownloadLoad(false);
      } catch (e) {
        toast.error('Something wrong');
        setDownloadLoad(false);
      }
    } else {
      setRequestLoading(true);

      setQuery(dataRequest);

      await dispatch(getDashboardSyncPakVsSortPakRequest({ ...dataRequest }));

      setRequestLoading(false);
      firstLoad = true;
    }
  }, [requestData]);

  const clearFilters = useCallback(() => {
    setRequestData({
      state: '',
      start: thisMonthStartDay,
      end: thisMonthEndDate,
    });

    setChartType('patient');
    setQuery({});

    dispatch(getDashboardSyncPakVsSortPakRequest({}));
  }, []);

  const selectType = (type) => {
    setCurrentCheckedTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((p) => p !== type);
      }

      return [...prev, type];
    });
  };

  const changeHealthSelect = useCallback((option) => {
    setRequestData((prev) => ({ ...prev, health: option.id, healthName: option.name }));
  }, []);

  const nextOptionListRequest = useCallback(async (healthPage) => {
    await dispatch(getHealthRequest({ page: healthPage, limit: 20, userId: accountHelpers.getUser().id }));
  }, []);

  return (
    <div className={classes.wrapper}>
      <SyncPakVsSortPakFilters
        onChangeDate={onChangeDate}
        nextOptionListRequest={nextOptionListRequest}
        clearFilters={clearFilters}
        filtersData={requestData}
        updateFilters={updateFilters}
        setFilterData={setRequestData}
        healthList={selectHealthList}
        healthTotalPage={healthTotalPage}
        healthCurrentPage={healthCurrentPage}
        changeHealthSelect={changeHealthSelect}
        downloadLoading={downloadLoad}
      />

      <div className={classes.dashboard_sync_wrapper}>
        <div className={classes.dashboard_growth_left_block}>
          <div className={classes.chart_header_wrapper}>
            <div className={classes.chart_type_wrapper}>
              <FilterButton
                onClick={setChartType}
                activeValue={chartType}
                buttonsData={typeDataLeft}
                height={40}
                fontWeight={600}
              />
            </div>

            <div className={classes.chart_header_text_wrapper}>
              <p>Total:</p>

              <div className={classes.chart_header_text}>
                <div>
                  <img src={specType[1].image} alt="icon" />
                </div>

                <p>{specType[1].name}</p>

                <p>-</p>

                <p>{chartType === 'patient' ? totalSortPak?.patient : totalSortPak?.script}</p>
              </div>

              <div className={classes.chart_header_text}>
                <div>
                  <img src={specType[0].image} alt="icon" />
                </div>

                <p>{specType[0].name}</p>

                <p>-</p>

                <p>{chartType === 'patient' ? totalSyncPak?.patient : totalSyncPak?.script}</p>
              </div>
            </div>

          </div>

          <div className={classes.chart_wrapper}>
            {!chartLoading && !requestLoading
              ? (chart.series?.[0]?.data?.length
                ? (
                  <Chart
                    options={chart.options}
                    series={chart.series}
                    width="100%"
                    height={500}
                  />
                )

                : (
                  <div className={classes.chart_is_empty}>No data to display</div>
                ))

              : <Loader size={40} className={classes.chart_wrapper_loader} />}
          </div>
        </div>

        <div className={classes.dashboard_growth_right_block}>
          {currentTypes.map((o) => {
            const {
              name,
              color,
              image,
              figure,
            } = specType.find((t) => t.name === o);

            return (
              <div
                key={name}
                className={`${classes.dashboard_growth_type_wrapper} ${!currentCheckedTypes.includes(name)
                  ? classes.disable
                  : ''}`}
                onClick={() => selectType(name)}
              >
                <div
                  style={{ background: color }}
                  className={`${classes.dashboard_growth_type_icon} ${figure === 'triangle'
                    ? classes.triangle : figure === 'square' ? classes.square : ''}`}
                >
                  <img src={image} alt="icon" />
                </div>

                <div className={classes.dashboard_growth_type_name}>
                  {name}
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default SyncPakVsSortPak;
